import React, { useState, useRef, useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import { Dropdown, Input, InputNumber, Modal } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  LinkOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  EditOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  BorderOuterOutlined,
  AppstoreAddOutlined,
  DownOutlined,
} from "@ant-design/icons";

const passthroughAttr = (name: string) => ({
  default: null,
  parseHTML: (el: HTMLElement) => el.getAttribute(name),
  renderHTML: (attrs: any) =>
    attrs?.[name] !== undefined && attrs?.[name] !== null
      ? { [name]: attrs[name] }
      : {},
});

const withStyleAttribute = <T extends { extend: Function }>(extension: T): T =>
  (extension as any).extend({
    addAttributes() {
      return {
        ...(this as any).parent?.(),
        style: passthroughAttr("style"),
      };
    },
  });

const withTableAttributes = <T extends { extend: Function }>(extension: T): T =>
  (extension as any).extend({
    addAttributes() {
      return {
        ...(this as any).parent?.(),
        style: passthroughAttr("style"),
        class: passthroughAttr("class"),
        bgcolor: passthroughAttr("bgcolor"),
        align: passthroughAttr("align"),
        valign: passthroughAttr("valign"),
        width: passthroughAttr("width"),
      };
    },
  });

const TableWithStyle = withTableAttributes(Table);
const TableRowWithStyle = withTableAttributes(TableRow);
const TableHeaderWithStyle = withTableAttributes(TableHeader);
const TableCellWithStyle = withTableAttributes(TableCell);
const LinkWithStyle = withStyleAttribute(Link);


interface EditableZoneProps {
  label: string;
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  onInsertVariable?: (placeholder: string) => void;
  themePrimary?: string;
  allowRichInserts?: boolean;
}


const createZoneExtensions = (
  placeholderText: string,
  allowRichInserts: boolean,
) => {
  const base: any[] = [
    StarterKit.configure({
      bulletList: { keepMarks: true, keepAttributes: false },
      orderedList: { keepMarks: true, keepAttributes: false },
    }),
    TextStyle,
    Color.configure({ types: ["textStyle"] }),
    LinkWithStyle.configure({
      openOnClick: false,
      autolink: true,
      defaultProtocol: "https",
      validate: () => true,
    }),
    Underline,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
    Placeholder.configure({ placeholder: placeholderText }),
  ];
  if (allowRichInserts) {
    base.push(
      TableWithStyle.configure({
        resizable: false,
        HTMLAttributes: { class: "et-table" },
      }),
      TableRowWithStyle,
      TableHeaderWithStyle,
      TableCellWithStyle,
    );
  }
  return base;
};


const buildCtaHtml = (label: string, href: string, primary: string): string => {
  const safeHref = href.trim();
  const withProtocol = /^(https?:|mailto:|tel:|#|\{\{)/i.test(safeHref)
    ? safeHref
    : `https://${safeHref}`;
  return `<p style="text-align:center;margin:24px 0;"><a href="${withProtocol}" style="display:inline-block;padding:13px 30px;background:${primary};color:#ffffff;text-decoration:none;border-radius:6px;font-weight:600;font-size:14px;letter-spacing:0.02em;">${label}</a></p>`;
};

const buildTableHtml = (rows: number, cols: number, primary: string): string => {
  const safeRows = Math.max(1, Math.min(20, Math.floor(rows)));
  const safeCols = Math.max(1, Math.min(8, Math.floor(cols)));
  const headerCells = Array.from({ length: safeCols })
    .map(
      (_, i) =>
        `<th style="padding:10px 14px;text-align:left;font:600 11px/16px Inter,Arial,sans-serif;color:#475569;text-transform:uppercase;letter-spacing:0.08em;border-bottom:1px solid #e2e8f0;background:#f1f5f9;">Header ${i + 1}</th>`,
    )
    .join("");
  const bodyRows = Array.from({ length: safeRows })
    .map(
      () =>
        `<tr>${Array.from({ length: safeCols })
          .map(
            () =>
              `<td style="padding:14px;font:400 13px/20px Inter,Arial,sans-serif;color:#0f172a;border-bottom:1px solid #e2e8f0;">Cell</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");
  return `<table style="width:100%;border-collapse:separate;border-spacing:0;border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin:16px 0;border-top:3px solid ${primary};"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table><p></p>`;
};

const EditableZone: React.FC<EditableZoneProps> = ({
  label,
  content,
  onChange,
  placeholder = "Click to edit this section…",
  themePrimary = "#3730a3",
  allowRichInserts = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [ctaModalOpen, setCtaModalOpen] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [tableModalOpen, setTableModalOpen] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const initialContentRef = useRef(content);

  
  const editor = useEditor({
    extensions: createZoneExtensions(placeholder, allowRichInserts),
    content,
    editorProps: {
      attributes: {
        class: "tiptap-email-zone-editor",
      },
    },
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
  });

  
  useEffect(() => {
    if (editor && content !== initialContentRef.current) {
      const currentHtml = editor.getHTML();
      if (currentHtml !== content) {
        editor.commands.setContent(content, { emitUpdate: false });
      }
      initialContentRef.current = content;
    }
  }, [content, editor]);

  
  useEffect(() => {
    if (!editing) return;
    const ANTD_OVERLAY_SELECTOR =
      ".ant-modal-root,.ant-modal-wrap,.ant-modal-mask,.ant-dropdown-root,.ant-dropdown,.ant-popover-root,.ant-popover,.ant-select-dropdown,.ant-picker-dropdown,.ant-tooltip,.ant-drawer";
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(ANTD_OVERLAY_SELECTOR)) return;
      if (wrapperRef.current && !wrapperRef.current.contains(target as Node)) {
        setEditing(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setEditing(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [editing]);

  useEffect(() => {
    if (!editing) {
      setLinkModalOpen(false);
      setCtaModalOpen(false);
      setTableModalOpen(false);
    }
  }, [editing]);

  
  useEffect(() => {
    if (editing && editor) {
      setTimeout(() => editor.commands.focus("end"), 50);
    }
  }, [editing, editor]);

  
  const TBtn: React.FC<{
    onClick: () => void;
    active?: boolean;
    title: string;
    children: React.ReactNode;
  }> = ({ onClick, active, title, children }) => (
    <button
      type="button"
      className={`zone-toolbar-btn${active ? " zone-toolbar-btn--active" : ""}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      title={title}
    >
      {children}
    </button>
  );

  
  if (!editing) {
    return (
      <div
        className="editable-zone editable-zone--view"
        onClick={() => setEditing(true)}
      >
        <span className="zone-label">{label}</span>
        <span className="zone-edit-hint">
          <EditOutlined />
        </span>
        <div
          className="zone-content"
          dangerouslySetInnerHTML={{ __html: content || `<p style="color:#9ca3af;font-style:italic">${placeholder}</p>` }}
        />
      </div>
    );
  }

  
  return (
    <div
      className="editable-zone editable-zone--edit"
      ref={wrapperRef}
    >
      <span className="zone-label">{label}</span>

      
      {editor && (
        <div className="zone-toolbar">
          <TBtn
            onClick={() => editor.chain().focus().toggleBold().run()}
            active={editor.isActive("bold")}
            title="Bold"
          >
            <BoldOutlined />
          </TBtn>
          <TBtn
            onClick={() => editor.chain().focus().toggleItalic().run()}
            active={editor.isActive("italic")}
            title="Italic"
          >
            <ItalicOutlined />
          </TBtn>
          <TBtn
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            active={editor.isActive("underline")}
            title="Underline"
          >
            <UnderlineOutlined />
          </TBtn>

          <span className="zone-toolbar-divider" />

          <TBtn
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            active={editor.isActive("bulletList")}
            title="Bullet List"
          >
            <UnorderedListOutlined />
          </TBtn>
          <TBtn
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            active={editor.isActive("orderedList")}
            title="Numbered List"
          >
            <OrderedListOutlined />
          </TBtn>

          <span className="zone-toolbar-divider" />

          <TBtn
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            active={editor.isActive({ textAlign: "left" })}
            title="Align Left"
          >
            <AlignLeftOutlined />
          </TBtn>
          <TBtn
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            active={editor.isActive({ textAlign: "center" })}
            title="Align Center"
          >
            <AlignCenterOutlined />
          </TBtn>
          <TBtn
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            active={editor.isActive({ textAlign: "right" })}
            title="Align Right"
          >
            <AlignRightOutlined />
          </TBtn>

          <span className="zone-toolbar-divider" />

          <TBtn
            onClick={() => {
              const existing = editor.getAttributes("link").href || "";
              setLinkUrl(existing);
              setLinkModalOpen(true);
            }}
            active={editor.isActive("link")}
            title="Insert Link"
          >
            <LinkOutlined />
          </TBtn>

          {allowRichInserts ? (
            <>
              <TBtn
                onClick={() => {
                  setCtaLabel("View details");
                  setCtaUrl("");
                  setCtaModalOpen(true);
                }}
                title="Insert call-to-action button"
              >
                <AppstoreAddOutlined />
              </TBtn>
              <TBtn
                onClick={() => {
                  setTableRows(3);
                  setTableCols(3);
                  setTableModalOpen(true);
                }}
                title="Insert table"
              >
                <BorderOuterOutlined />
              </TBtn>
              {editor.isActive("table") ? (
                <>
                  <span className="zone-toolbar-divider" />
                  <Dropdown
                    trigger={["click"]}
                    placement="bottomRight"
                    menu={{
                      items: [
                        {
                          key: "addRowAfter",
                          label: "Add row below",
                          onClick: () =>
                            editor.chain().focus().addRowAfter().run(),
                        },
                        {
                          key: "addRowBefore",
                          label: "Add row above",
                          onClick: () =>
                            editor.chain().focus().addRowBefore().run(),
                        },
                        {
                          key: "addColAfter",
                          label: "Add column right",
                          onClick: () =>
                            editor.chain().focus().addColumnAfter().run(),
                        },
                        {
                          key: "addColBefore",
                          label: "Add column left",
                          onClick: () =>
                            editor.chain().focus().addColumnBefore().run(),
                        },
                        { type: "divider" as const },
                        {
                          key: "deleteRow",
                          label: "Delete row",
                          onClick: () =>
                            editor.chain().focus().deleteRow().run(),
                        },
                        {
                          key: "deleteCol",
                          label: "Delete column",
                          onClick: () =>
                            editor.chain().focus().deleteColumn().run(),
                        },
                        { type: "divider" as const },
                        {
                          key: "deleteTable",
                          danger: true,
                          label: "Delete entire table",
                          onClick: () =>
                            editor.chain().focus().deleteTable().run(),
                        },
                      ],
                    }}
                  >
                    <button
                      type="button"
                      className="insert-var-btn"
                      title="Table actions"
                    >
                      <BorderOuterOutlined style={{ fontSize: 11 }} />
                      Table
                      <DownOutlined style={{ fontSize: 9, marginLeft: 2 }} />
                    </button>
                  </Dropdown>
                </>
              ) : null}
            </>
          ) : null}


        </div>
      )}

      
      <div className="zone-editor-wrapper">
        <EditorContent editor={editor} />
      </div>

      <Modal
        open={linkModalOpen}
        title={
          <span>
            <LinkOutlined /> Insert link
          </span>
        }
        okText={editor?.isActive("link") ? "Update" : "Insert"}
        cancelText="Cancel"
        onCancel={() => {
          setLinkModalOpen(false);
          setLinkUrl("");
        }}
        onOk={() => {
          const trimmed = linkUrl.trim();
          if (!trimmed) {
            editor?.chain().focus().unsetLink().run();
          } else {
            const withProtocol = /^(https?:|mailto:|tel:|#|\{\{)/i.test(trimmed)
              ? trimmed
              : `https://${trimmed}`;
            editor?.chain().focus().extendMarkRange("link").setLink({ href: withProtocol }).run();
          }
          setLinkModalOpen(false);
          setLinkUrl("");
        }}
        zIndex={1200}
        destroyOnClose
        width={460}
      >
        <Input
          autoFocus
          placeholder="https://example.com  or  {{dashboardLink}}"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          onPressEnter={(e) => {
            e.preventDefault();
            (e.currentTarget.closest(".ant-modal-content")
              ?.querySelector(".ant-modal-footer .ant-btn-primary") as HTMLButtonElement | null)
              ?.click();
          }}
          prefix={<LinkOutlined style={{ color: "#94a3b8" }} />}
          allowClear
        />
        <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
          Use a variable like <code>{`{{dashboardLink}}`}</code> for dynamic URLs — the backend fills it at send time. Leave empty and confirm to remove the link.
        </div>
      </Modal>

      <Modal
        open={ctaModalOpen}
        title={
          <span>
            <AppstoreAddOutlined /> Insert button
          </span>
        }
        okText="Insert"
        cancelText="Cancel"
        onCancel={() => setCtaModalOpen(false)}
        onOk={() => {
          const label = ctaLabel.trim() || "Open";
          const href = ctaUrl.trim() || "#";
          editor?.chain().focus().insertContent(buildCtaHtml(label, href, themePrimary)).run();
          setCtaModalOpen(false);
        }}
        zIndex={1200}
        destroyOnClose
        width={480}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
            Button label
          </div>
          <Input
            autoFocus
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder="View details"
            allowClear
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
            Link URL
          </div>
          <Input
            value={ctaUrl}
            onChange={(e) => setCtaUrl(e.target.value)}
            placeholder="https://example.com  or  {{dashboardLink}}"
            prefix={<LinkOutlined style={{ color: "#94a3b8" }} />}
            allowClear
          />
          <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
            Use a variable like <code>{`{{dashboardLink}}`}</code> for dynamic URLs — the backend fills it at send time. Button adopts the template's theme color.
          </div>
        </div>
      </Modal>

      <Modal
        open={tableModalOpen}
        title={
          <span>
            <BorderOuterOutlined /> Insert table
          </span>
        }
        okText="Insert"
        cancelText="Cancel"
        onCancel={() => setTableModalOpen(false)}
        onOk={() => {
          editor
            ?.chain()
            .focus()
            .insertContent(buildTableHtml(tableRows, tableCols, themePrimary))
            .run();
          setTableModalOpen(false);
        }}
        zIndex={1200}
        destroyOnClose
        width={420}
      >
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
              Rows
            </div>
            <InputNumber
              autoFocus
              value={tableRows}
              onChange={(v) => setTableRows(Number(v) || 1)}
              min={1}
              max={20}
              style={{ width: "100%" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#475569", marginBottom: 4 }}>
              Columns
            </div>
            <InputNumber
              value={tableCols}
              onChange={(v) => setTableCols(Number(v) || 1)}
              min={1}
              max={8}
              style={{ width: "100%" }}
            />
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
          Inserts a themed header row + cells you can edit inline.
        </div>
      </Modal>
    </div>
  );
};

export default EditableZone;
